import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { AlertCircle, CheckCircle2, ExternalLink, FileText, Lock, Plus, Save, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import {
  usePlannedRevenues,
  useCreatePlannedRevenue,
  useUpdatePlannedRevenue,
  useDeletePlannedRevenue,
  useConvertPlannedRevenueToDraft,
} from "@/features/finances/hooks/useFinances";
import { useAllSubTasks } from "@/features/tasks/hooks/useSubTasks";
import { TASK_STATUS_LABELS } from "@/features/tasks/types";
import type { PlannedRevenue } from "@/features/finances/types";

interface Props {
  projectId: string;
  canEdit: boolean;
  openId?: string | null;
}

const emptyCreateForm = { description: "", amount: "", expected_date: "" };

// ── Revenue detail / edit panel ────────────────────────────────────────────

interface DetailPanelProps {
  revenue: PlannedRevenue;
  projectId: string;
  canEdit: boolean;
  onClose: () => void;
}

function RevenueDetailPanel({ revenue, projectId, canEdit, onClose }: DetailPanelProps) {
  const user = useAuthStore((s) => s.user);
  const canViewInvoicePrz =
    user?.role === "accountant" ||
    (user?.role === "director" && (!!user.is_technical_director || !!user.is_ceo));
  const updateMutation = useUpdatePlannedRevenue(projectId);
  const convertMutation = useConvertPlannedRevenueToDraft(projectId);

  // All subtasks for this project — for the blocking tasks picker
  const subtaskFilters = useMemo(() => ({ project: [projectId] }), [projectId]);
  const { data: allSubtasks = [] } = useAllSubTasks(subtaskFilters);

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    description: revenue.description,
    notes: revenue.notes,
    amount: revenue.amount,
    expected_date: revenue.expected_date,
  });
  const [err, setErr] = useState<string | null>(null);
  const [subtaskSearch, setSubtaskSearch] = useState("");

  // IDs currently linked as blocking subtasks
  const [blockingIds, setBlockingIds] = useState<string[]>(
    revenue.blocking_subtasks.map((s) => s.id)
  );

  const isDirty =
    form.description !== revenue.description ||
    form.notes !== revenue.notes ||
    form.amount !== revenue.amount ||
    form.expected_date !== revenue.expected_date ||
    JSON.stringify(blockingIds.slice().sort()) !==
      JSON.stringify(revenue.blocking_subtasks.map((s) => s.id).sort());

  const handleSave = async () => {
    if (!form.description.trim() || !form.amount || !form.expected_date) {
      setErr("Opis, kwota i data są wymagane.");
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: revenue.id,
        data: {
          description: form.description,
          notes: form.notes,
          amount: form.amount,
          expected_date: form.expected_date,
          blocking_subtask_ids: blockingIds,
        },
      });
      setEditMode(false);
      setErr(null);
    } catch {
      setErr("Błąd zapisu.");
    }
  };

  const [convertDialog, setConvertDialog] = useState<{ isAdvance: boolean } | null>(null);

  const handleConvertConfirm = async (isAdvance: boolean) => {
    setConvertDialog(null);
    try { await convertMutation.mutateAsync({ id: revenue.id, isAdvance }); }
    catch { setErr("Błąd tworzenia faktury."); }
  };

  const availableSubtasks = useMemo(
    () =>
      allSubtasks.filter(
        (s) =>
          !blockingIds.includes(s.id) &&
          (subtaskSearch === "" ||
            s.title.toLowerCase().includes(subtaskSearch.toLowerCase()) ||
            s.task_title.toLowerCase().includes(subtaskSearch.toLowerCase()))
      ),
    [allSubtasks, blockingIds, subtaskSearch]
  );

  const linkedSubtaskDetails = useMemo(
    () => allSubtasks.filter((s) => blockingIds.includes(s.id)),
    [allSubtasks, blockingIds]
  );

  const addBlocking = (id: string) => setBlockingIds((prev) => [...prev, id]);
  const removeBlocking = (id: string) => setBlockingIds((prev) => prev.filter((x) => x !== id));

  const isAuto = revenue.is_auto_generated;
  const canModify = canEdit && !isAuto;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white dark:bg-gray-900 h-full shadow-xl flex flex-col overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Planowany przychód</span>
            {isAuto && <Badge color="blue">Auto</Badge>}
            {revenue.is_blocked && !isAuto && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                <Lock size={10} /> Zablokowany
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 px-5 py-4 space-y-5">
          {/* Description */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Opis</label>
            {editMode ? (
              <input
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-gray-100">{revenue.description}</p>
            )}
          </div>

          {/* Amount + Date row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Kwota netto (PLN)</label>
              {editMode ? (
                <input
                  type="number"
                  step="0.01"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              ) : (
                <p className="text-sm font-semibold text-green-700">+{formatAmount(revenue.amount)} PLN</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Planowana data</label>
              {editMode ? (
                <DatePicker
                  className="text-gray-900 dark:text-gray-100"
                  value={form.expected_date}
                  onChange={(v) => setForm({ ...form, expected_date: v })}
                />
              ) : (
                <p className="text-sm text-gray-700 dark:text-gray-300">{formatDate(revenue.expected_date)}</p>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Notatki</label>
            {editMode ? (
              <textarea
                rows={3}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Dodatkowe informacje..."
              />
            ) : revenue.notes ? (
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{revenue.notes}</p>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic">Brak notatek.</p>
            )}
          </div>

          {/* Draft invoice */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Faktura PRZ</label>
            {revenue.draft_invoice_id ? (
              canViewInvoicePrz ? (
                <Link
                  to={`/finances/invoices-prz/${revenue.draft_invoice_id}`}
                  className="inline-flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:underline"
                >
                  <FileText size={14} />
                  {revenue.draft_invoice_number || "Szkic"}
                  <ExternalLink size={12} />
                </Link>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                  <FileText size={14} />
                  {revenue.draft_invoice_number || "Szkic"}
                </span>
              )
            ) : isAuto ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">Auto-przychody nie mogą mieć faktur.</p>
            ) : canEdit ? (
              <>
                <Button size="sm" variant="secondary" loading={convertMutation.isPending} onClick={() => setConvertDialog({ isAdvance: false })}>
                  <FileText size={14} className="mr-1" /> Utwórz szkic faktury PRZ
                </Button>
                {convertDialog && (
                  <div className="mt-2 p-3 bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] border border-[var(--color-primary)] dark:border-[var(--color-primary)] rounded-lg flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={convertDialog.isAdvance}
                        onChange={(e) => setConvertDialog({ isAdvance: e.target.checked })}
                        className="w-4 h-4 accent-[var(--color-primary)]"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Faktura zaliczkowa</span>
                    </label>
                    <div className="flex gap-2">
                      <Button size="sm" loading={convertMutation.isPending} onClick={() => handleConvertConfirm(convertDialog.isAdvance)}>Utwórz szkic</Button>
                      <Button size="sm" variant="secondary" onClick={() => setConvertDialog(null)}>Anuluj</Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">Brak powiązanej faktury.</p>
            )}
          </div>

          {/* Blocking subtasks */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-2">
              Zadania blokujące fakturowanie
            </label>

            {/* Linked subtasks */}
            {linkedSubtaskDetails.length > 0 ? (
              <div className="space-y-1 mb-3">
                {linkedSubtaskDetails.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-md px-3 py-1.5 text-xs"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {s.status === "done" ? (
                        <CheckCircle2 size={13} className="text-green-500 flex-shrink-0" />
                      ) : (
                        <AlertCircle size={13} className="text-amber-500 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <span className="text-gray-500 dark:text-gray-400">{s.task_title} / </span>
                        <span className="text-gray-800 dark:text-gray-200 font-medium">{s.title}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${s.status === "done" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {TASK_STATUS_LABELS[s.status as keyof typeof TASK_STATUS_LABELS] || s.status}
                      </span>
                      {canModify && editMode && (
                        <button
                          onClick={() => removeBlocking(s.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic mb-3">
                Brak powiązanych zadań blokujących.
              </p>
            )}

            {/* Add subtask picker — only in edit mode */}
            {canModify && editMode && (
              <div>
                <input
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 mb-1"
                  placeholder="Szukaj zadania..."
                  value={subtaskSearch}
                  onChange={(e) => setSubtaskSearch(e.target.value)}
                />
                {subtaskSearch && (
                  <div className="rounded-md bg-white dark:bg-gray-800 max-h-40 overflow-y-auto">
                    {availableSubtasks.length === 0 ? (
                      <p className="text-xs text-gray-400 px-3 py-2">Brak wyników.</p>
                    ) : (
                      availableSubtasks.slice(0, 20).map((s) => (
                        <button
                          key={s.id}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1.5"
                          onClick={() => { addBlocking(s.id); setSubtaskSearch(""); }}
                        >
                          <Plus size={11} className="text-gray-400 flex-shrink-0" />
                          <span className="text-gray-500 dark:text-gray-400">{s.task_title} / </span>
                          <span className="text-gray-800 dark:text-gray-200">{s.title}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {err && <p className="text-xs text-red-600">{err}</p>}
        </div>

        {/* Footer */}
        {canModify && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex gap-2 flex-shrink-0">
            {editMode ? (
              <>
                <Button
                  size="sm"
                  loading={updateMutation.isPending}
                  disabled={!isDirty}
                  onClick={handleSave}
                >
                  <Save size={13} className="mr-1" /> Zapisz
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setForm({
                      description: revenue.description,
                      notes: revenue.notes,
                      amount: revenue.amount,
                      expected_date: revenue.expected_date,
                    });
                    setBlockingIds(revenue.blocking_subtasks.map((s) => s.id));
                    setEditMode(false);
                    setErr(null);
                  }}
                >
                  Anuluj
                </Button>
              </>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setEditMode(true)}>
                Edytuj
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────

export function PlannedRevenuesPanel({ projectId, canEdit, openId }: Props) {
  const { data: allRevenues = [], isLoading } = usePlannedRevenues(projectId);
  // Hide revenues whose linked invoice is fully paid; show partial/unpaid with correct amount
  const revenues = allRevenues.filter(
    (r) => !r.draft_invoice_id || r.draft_invoice_payment_status !== "paid"
  );
  const createMutation = useCreatePlannedRevenue(projectId);
  const deleteMutation = useDeletePlannedRevenue(projectId);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [selectedRevenue, setSelectedRevenue] = useState<PlannedRevenue | null>(null);

  // When openId is provided (e.g. from cashflow click), auto-open that revenue
  useEffect(() => {
    if (openId && allRevenues.length > 0) {
      const target = allRevenues.find((r) => r.id === openId);
      if (target) setSelectedRevenue(target);
    }
  }, [openId, allRevenues]);

  // Keep selected revenue in sync when data refreshes (use allRevenues to allow detail on any)
  const liveSelected = selectedRevenue
    ? allRevenues.find((r) => r.id === selectedRevenue.id) ?? selectedRevenue
    : null;

  const handleCreate = async () => {
    if (!createForm.description.trim() || !createForm.amount || !createForm.expected_date) {
      setCreateErr("Opis, kwota i data są wymagane.");
      return;
    }
    try {
      await createMutation.mutateAsync({ project: projectId, ...createForm });
      setCreateForm(emptyCreateForm);
      setShowCreateForm(false);
      setCreateErr(null);
    } catch {
      setCreateErr("Błąd zapisu.");
    }
  };

  const handleDelete = (id: string, isAuto: boolean) => {
    if (isAuto) {
      alert("Nie można usunąć automatycznego przychodu. Zmień wartość lub datę końcową projektu.");
      return;
    }
    if (!confirm("Usunąć planowany przychód?")) return;
    deleteMutation.mutate(id);
  };

  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <div>
      {liveSelected && (
        <RevenueDetailPanel
          revenue={liveSelected}
          projectId={projectId}
          canEdit={canEdit}
          onClose={() => setSelectedRevenue(null)}
        />
      )}

      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h3 className="font-semibold text-gray-800 dark:text-gray-200">Planowane przychody</h3>
        {canEdit && (
          <Button size="sm" onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? "Anuluj" : "+ Dodaj przychód"}
          </Button>
        )}
      </div>

      {showCreateForm && (
        <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="col-span-3 md:col-span-1">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Opis *</label>
            <input
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              placeholder="np. Faktura częściowa"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Kwota netto (PLN) *</label>
            <input
              type="number"
              step="0.01"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
              value={createForm.amount}
              onChange={(e) => setCreateForm({ ...createForm, amount: e.target.value })}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Planowana data *</label>
            <DatePicker
              value={createForm.expected_date}
              onChange={(v) => setCreateForm({ ...createForm, expected_date: v })}
            />
          </div>
          {createErr && <p className="col-span-3 text-xs text-red-600">{createErr}</p>}
          <div className="col-span-3 flex gap-2">
            <Button size="sm" loading={createMutation.isPending} onClick={handleCreate}>Zapisz</Button>
            <Button size="sm" variant="secondary" onClick={() => { setShowCreateForm(false); setCreateErr(null); }}>Anuluj</Button>
          </div>
        </div>
      )}

      {revenues.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak planowanych przychodów.</p>
      ) : (
        <div className="bg-white dark:bg-gray-800">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Opis</th>
                <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Data</th>
                <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kwota</th>
                <th className="hidden sm:table-cell text-center px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                {canEdit && <th className="px-3 py-2 w-10 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {revenues.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                  onClick={() => setSelectedRevenue(r)}
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-800 dark:text-gray-200">{r.description}</span>
                      {r.draft_invoice_id && (
                        <span title="Powiązana faktura PRZ">
                          <FileText size={13} className="text-[var(--color-primary)]" />
                        </span>
                      )}
                      {r.is_blocked && !r.is_auto_generated && (
                        <span title="Zablokowany przez niezakończone zadania">
                          <Lock size={13} className="text-amber-500" />
                        </span>
                      )}
                    </div>
                    {r.blocking_subtasks.length > 0 && (
                      <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {r.blocking_subtasks.filter((s) => s.status !== "done").length > 0
                          ? `${r.blocking_subtasks.filter((s) => s.status !== "done").length} nieukończ. zadań`
                          : "Wszystkie zadania ukończone"}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">{formatDate(r.expected_date)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.draft_invoice_payment_status === "partially_paid" && r.draft_invoice_remaining_net ? (
                      <div>
                        <span className="font-semibold text-amber-700">+{formatAmount(r.draft_invoice_remaining_net)} PLN</span>
                        <div className="text-xs text-amber-600 mt-0.5">pozostało (częściowo opłacona)</div>
                      </div>
                    ) : (
                      <span className="font-semibold text-green-700">+{formatAmount(r.amount)} PLN</span>
                    )}
                  </td>
                  <td className="hidden sm:table-cell px-3 py-2 text-center">
                    <Badge color={r.is_auto_generated ? "blue" : "green"}>
                      {r.is_auto_generated ? "Auto" : "Ręczny"}
                    </Badge>
                  </td>
                  {canEdit && (
                    <td
                      className="px-2 py-2 text-right w-8"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => handleDelete(r.id, r.is_auto_generated)}
                        className={r.is_auto_generated ? "text-gray-300 cursor-not-allowed" : "text-red-400 hover:text-red-600"}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
